import { useCallback, useEffect, useRef, useState } from 'react';
import { newMessageId } from '../InlineChat';
import type { Message } from '../InlineChat';
import {
  MAX_PARALLEL_BACKGROUND_JOBS,
  buildBackgroundJobSnapshot,
  loadBackgroundJobQueue,
  runBackgroundDocumentAnalysis,
  saveBackgroundJobQueue,
  shortDocumentLabel,
  threadHasBackgroundJobResult,
  type BackgroundJobCompletionNotice,
  type BackgroundJobSnapshot,
} from './portalBackgroundJobs';

interface UsePortalBackgroundJobsArgs {
  customerId: string;
  webhookUrl: string;
  timeoutMs: number;
  mode?: string;
  extraBody?: Record<string, unknown>;
  getThreadMessages: (threadId: string) => Message[] | undefined;
  appendToThread: (threadId: string, updater: (prev: Message[]) => Message[]) => void;
}

export function usePortalBackgroundJobs({
  customerId,
  webhookUrl,
  timeoutMs,
  mode,
  extraBody,
  getThreadMessages,
  appendToThread,
}: UsePortalBackgroundJobsArgs) {
  const [pendingJobs, setPendingJobs] = useState<BackgroundJobSnapshot[]>(() =>
    loadBackgroundJobQueue(customerId),
  );
  const [completionNotices, setCompletionNotices] = useState<BackgroundJobCompletionNotice[]>([]);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const cancelledRef = useRef<Set<string>>(new Set());
  const runningRef = useRef<Set<string>>(new Set());
  const resumeStartedRef = useRef(false);

  const persistQueue = useCallback((jobs: BackgroundJobSnapshot[]) => {
    saveBackgroundJobQueue(customerId, jobs);
  }, [customerId]);

  const removeJob = useCallback((requestId: string) => {
    setPendingJobs((prev) => {
      const next = prev.filter((job) => job.request_id !== requestId);
      persistQueue(next);
      return next;
    });
  }, [persistQueue]);

  const pushNotice = useCallback((notice: BackgroundJobCompletionNotice) => {
    setCompletionNotices((prev) => [...prev, notice]);
  }, []);

  const dismissNotice = useCallback((id: string) => {
    setCompletionNotices((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const executeJob = useCallback((job: BackgroundJobSnapshot) => {
    if (runningRef.current.has(job.request_id)) return;
    if (threadHasBackgroundJobResult(getThreadMessages(job.threadId), job.request_id)) {
      removeJob(job.request_id);
      return;
    }

    runningRef.current.add(job.request_id);
    const controller = new AbortController();
    controllersRef.current.set(job.request_id, controller);
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);

    void (async () => {
      const outcome = await runBackgroundDocumentAnalysis({
        job,
        webhookUrl,
        timeoutMs,
        signal: controller.signal,
        isCancelled: () => cancelledRef.current.has(job.request_id),
      });

      window.clearTimeout(timer);
      controllersRef.current.delete(job.request_id);
      cancelledRef.current.delete(job.request_id);
      runningRef.current.delete(job.request_id);
      removeJob(job.request_id);

      const { documentName, threadId, request_id: requestId } = job;

      if (outcome.kind === 'success') {
        appendToThread(threadId, (prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Hintergrundanalyse „${documentName}“ abgeschlossen:\n\n${outcome.reply}`,
            id: newMessageId(),
            reasoningTrace: outcome.trace,
          },
        ]);
        pushNotice({
          id: `bg_done_${requestId}`,
          threadId,
          documentName,
          success: true,
          summary: `Analyse „${shortDocumentLabel(documentName)}“ abgeschlossen`,
        });
        return;
      }

      if (outcome.cancelled) {
        appendToThread(threadId, (prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Die Hintergrundanalyse „${documentName}“ wurde abgebrochen.`,
            id: newMessageId(),
            reasoningTrace: outcome.trace,
          },
        ]);
        return;
      }

      appendToThread(threadId, (prev) => [
        ...prev,
        {
          role: 'bot',
          text: outcome.timedOut
            ? `Die Hintergrundanalyse „${documentName}“ hat das Zeitlimit erreicht. Bitte erneut starten.`
            : `Die Hintergrundanalyse „${documentName}“ ist fehlgeschlagen. Bitte erneut versuchen.`,
          id: newMessageId(),
          reasoningTrace: outcome.trace,
        },
      ]);
      pushNotice({
        id: `bg_fail_${requestId}`,
        threadId,
        documentName,
        success: false,
        summary: outcome.timedOut
          ? `Analyse „${shortDocumentLabel(documentName)}“ — Zeitlimit`
          : `Analyse „${shortDocumentLabel(documentName)}“ fehlgeschlagen`,
      });
    })();
  }, [
    appendToThread,
    getThreadMessages,
    pushNotice,
    removeJob,
    timeoutMs,
    webhookUrl,
  ]);

  const startSlots = useCallback(() => {
    setPendingJobs((current) => {
      const runnable = current.filter((job) => !runningRef.current.has(job.request_id));
      const slots = MAX_PARALLEL_BACKGROUND_JOBS - runningRef.current.size;
      if (slots <= 0 || runnable.length === 0) return current;
      runnable.slice(0, slots).forEach((job) => executeJob(job));
      return current;
    });
  }, [executeJob]);

  const enqueueJob = useCallback((args: {
    documentId: string;
    documentName: string;
    threadId: string;
    history: Message[];
    documentContext: string;
    openDocumentsMeta?: import('./portalDocuments').PortalOpenDocumentMeta[];
    selectedDocuments?: import('./portalDocuments').PortalSelectedDocumentPayload[];
    documentExtractFingerprint?: string | null;
  }): BackgroundJobSnapshot | null => {
    let created: BackgroundJobSnapshot | null = null;
    setPendingJobs((prev) => {
      if (prev.length >= MAX_PARALLEL_BACKGROUND_JOBS) return prev;
      const job = buildBackgroundJobSnapshot({
        ...args,
        extraBody,
        mode,
      });
      created = job;
      const next = [...prev, job];
      persistQueue(next);
      return next;
    });
    if (created) {
      const multi = args.selectedDocuments && args.selectedDocuments.length >= 2;
      const count = args.selectedDocuments?.length ?? 1;
      appendToThread(args.threadId, (prev) => [
        ...prev,
        {
          role: 'bot',
          text: multi
            ? `Analyse läuft im Hintergrund für ${count} ausgewählte Dokumente: ${args.documentName}. Du kannst währenddessen weiterarbeiten.`
            : `Analyse läuft im Hintergrund: ${args.documentName}. Du kannst währenddessen ein anderes Dokument öffnen.`,
          id: newMessageId(),
        },
      ]);
      window.setTimeout(startSlots, 0);
    }
    return created;
  }, [appendToThread, extraBody, mode, persistQueue, startSlots]);

  const cancelJob = useCallback((requestId: string) => {
    const controller = controllersRef.current.get(requestId);
    if (controller) {
      cancelledRef.current.add(requestId);
      controller.abort();
      return;
    }
    removeJob(requestId);
  }, [removeJob]);

  useEffect(() => {
    persistQueue(pendingJobs);
  }, [pendingJobs, persistQueue]);

  useEffect(() => {
    setPendingJobs(loadBackgroundJobQueue(customerId));
    resumeStartedRef.current = false;
  }, [customerId]);

  useEffect(() => {
    if (resumeStartedRef.current) return;
    resumeStartedRef.current = true;
    if (pendingJobs.length === 0) return;
    startSlots();
  }, [pendingJobs, startSlots]);

  useEffect(() => () => {
    controllersRef.current.forEach((controller) => controller.abort());
    controllersRef.current.clear();
    cancelledRef.current.clear();
    runningRef.current.clear();
  }, []);

  const canEnqueue = pendingJobs.length < MAX_PARALLEL_BACKGROUND_JOBS;
  const hasRunningJobs = pendingJobs.length > 0;

  return {
    pendingJobs,
    completionNotices,
    canEnqueue,
    hasRunningJobs,
    enqueueJob,
    cancelJob,
    dismissNotice,
  };
}
